/**
 * The one field recipe every form control in this app is built from.
 *
 * Before this existed each control hand-rolled its own surface and they drifted:
 * Input and Textarea were filled with `surface-input`, the SelectPopover trigger
 * was `bg-transparent`, and NumberInput / CurrencyInput / MarginInput were
 * transparent with no focus ring at all. Stacked in one form they read as
 * different components — which is exactly what a "Client name" input next to a
 * "Country" select looked like.
 *
 * `--surface-input` is the theme's dedicated field role, tuned so that "a field
 * wearing it reads as an input" (see the note beside it in @codezo/theme
 * tokens.css). A control that opts out of it is opting out of the only token
 * designed for this job, so these constants are the way to opt in.
 *
 * Compose with cn() and keep these FIRST, so a caller's own className still wins.
 */

/** Fill, border and radius — worn by every control that is a field. */
export const fieldSurface = 'rounded-lg border border-line-2 bg-surface-input transition-colors';

/** Default metrics: 32px tall, matching Button's default size. */
export const fieldSize = 'h-8 px-2.5 text-sm';

/** Focus ring for a control that is itself the focusable element. */
export const fieldFocus =
	'focus-visible:border-ring focus-visible:ring-[1.5px] focus-visible:ring-ring/50';

/**
 * Focus ring for a wrapper that owns the border while an inner <input> takes the
 * focus — NumberInput, CurrencyInput, MarginInput. Same ring, different trigger.
 */
export const fieldFocusWithin =
	'focus-within:border-ring focus-within:ring-[1.5px] focus-within:ring-ring/50';

/** Disabled treatment for the focusable element itself. */
export const fieldDisabled =
	'disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-surface-input/50 disabled:opacity-50';

/** Invalid treatment, driven by aria-invalid. */
export const fieldInvalid =
	'aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20';

/** Everything a plain single-line text field needs. */
export const fieldBase = `${fieldSurface} ${fieldSize} ${fieldFocus} ${fieldDisabled} ${fieldInvalid}`;

/**
 * A wrapper that draws the field while an inner input holds the value — the
 * split controls. Disabled/invalid live on the inner element, not here.
 */
export const fieldWrapper = `${fieldSurface} ${fieldFocusWithin} flex h-8 overflow-hidden`;

/** The bare inner input inside a `fieldWrapper` — no surface of its own. */
export const fieldWrapperInput =
	'min-w-0 flex-1 bg-transparent px-2.5 text-sm outline-none placeholder:text-muted-foreground';
